/**
 * Authentication API - V2
 * Provides authentication services for user login, registration, and session management
 */
import type { ApiResponse } from '@/types/api'
import type {
  AccountBindingInfo,
  AlipayBindUrlResponse,
  BindEmailRequest,
  BindPhoneRequest,
  ChangePasswordRequest,
  CodeResponse,
  ForgotPasswordCredentials,
  ImageCaptchaResponse,
  LoginCredentials,
  LoginResponse,
  PasswordResetVerifyRequest,
  PasswordResetVerifyResponse,
  PhoneLoginCredentials,
  RegisterCredentials,
  RegisterResponse,
  ResetPasswordRequest,
  SendBindingCodeRequest,
  TokenInfo,
  UnbindEmailRequest,
  UnbindPhoneRequest,
  UserInfo,
  WechatBindStatusResponse,
  WechatLoginResultResponse,
  WechatQrCodeResponse,
} from '@/types/auth'
import { wrapDelete, wrapGet, wrapPatch, wrapPost } from '@/utils/request'

export const authApi = {
  async login(credentials: LoginCredentials): Promise<ApiResponse<LoginResponse>> {
    const params = new URLSearchParams()
    params.append('grant_type', 'password')
    params.append('username', credentials.username)
    params.append('password', credentials.password)

    if (credentials.imageCaptcha) {
      params.append('imageCaptcha', credentials.imageCaptcha)
    }
    if (credentials.captchaId) {
      params.append('captchaId', credentials.captchaId)
    }
    if (credentials.rememberMe !== undefined) {
      params.append('rememberMe', credentials.rememberMe.toString())
    }

    return wrapPost('/auth/login/access-token', params, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    })
  },

  async phoneLogin(credentials: PhoneLoginCredentials): Promise<ApiResponse<LoginResponse>> {
    return wrapPost('/auth/phone-login', credentials)
  },

  async register(credentials: RegisterCredentials): Promise<ApiResponse<RegisterResponse>> {
    return wrapPost('/auth/register', credentials)
  },

  async getCurrentUser(): Promise<ApiResponse<UserInfo>> {
    return wrapGet('/users/me')
  },

  async getImageCaptcha(): Promise<ApiResponse<ImageCaptchaResponse>> {
    return wrapGet('/auth/captcha/image')
  },

  async sendVerificationCode(data: { phone: string, captchaId: string, captchaCode: string }): Promise<ApiResponse<CodeResponse>> {
    return wrapPost('/auth/send-code', data)
  },

  async updateProfile(data: Partial<UserInfo>): Promise<ApiResponse<UserInfo>> {
    return wrapPatch('/users/me/profile', data)
  },

  async uploadAvatar(formData: FormData): Promise<ApiResponse<UserInfo>> {
    return wrapPost('/users/me/avatar', formData)
  },

  async refreshToken(refreshToken: string): Promise<ApiResponse<TokenInfo>> {
    return wrapPost('/auth/refresh-token', { refreshToken })
  },

  async logout(): Promise<ApiResponse<void>> {
    return wrapPost('/auth/logout')
  },

  // Password Reset Methods
  async sendPasswordResetEmail(email: string): Promise<ApiResponse<void>> {
    return wrapPost('/auth/password/email', { account: email })
  },

  async resetPassword(data: ResetPasswordRequest): Promise<ApiResponse<void>> {
    return wrapPost('/auth/password/reset', {
      token: data.token,
      new_password: data.newPassword,
    })
  },

  async forgotPassword(credentials: ForgotPasswordCredentials): Promise<ApiResponse<void>> {
    return wrapPost('/auth/forgot-password', credentials)
  },

  // Password Reset - Send SMS Code
  async sendPasswordResetSms(phone: string): Promise<ApiResponse<void>> {
    return wrapPost('/auth/password/phone', { phone })
  },

  // Password Reset - Verify Code and Get Reset Token
  async verifyPasswordResetCode(data: PasswordResetVerifyRequest): Promise<ApiResponse<PasswordResetVerifyResponse>> {
    return wrapPost('/auth/password/verify', data)
  },

  // Account Security Methods
  async changePassword(data: ChangePasswordRequest): Promise<ApiResponse<void>> {
    return wrapPatch('/users/me/password', {
      currentPassword: data.oldPassword,
      newPassword: data.newPassword,
    })
  },

  async bindPhone(data: BindPhoneRequest): Promise<ApiResponse<void>> {
    return wrapPost('/users/me/bindings/phone', data)
  },

  async unbindPhone(_data: UnbindPhoneRequest): Promise<ApiResponse<void>> {
    return wrapDelete('/users/me/bindings/phone')
  },

  async bindEmail(data: BindEmailRequest): Promise<ApiResponse<void>> {
    return wrapPost('/users/me/bindings/email', data)
  },

  async unbindEmail(_data: UnbindEmailRequest): Promise<ApiResponse<void>> {
    return wrapDelete('/users/me/bindings/email')
  },

  async sendBindingCode(data: SendBindingCodeRequest): Promise<ApiResponse<CodeResponse>> {
    return wrapPost('/users/me/bindings/code', data)
  },

  // OAuth Binding Methods
  async getWechatBindQrCode(): Promise<ApiResponse<WechatQrCodeResponse>> {
    return wrapGet('/auth/wechat/bind/qrcode')
  },

  async checkWechatBindStatus(sceneId: string): Promise<ApiResponse<WechatBindStatusResponse>> {
    return wrapGet(`/auth/wechat/bind/status?sceneId=${sceneId}`)
  },

  async unbindWechat(): Promise<ApiResponse<void>> {
    return wrapDelete('/users/me/bindings/wechat')
  },

  async getAlipayBindUrl(): Promise<ApiResponse<AlipayBindUrlResponse>> {
    return wrapGet('/users/me/bindings/alipay/url')
  },

  async unbindAlipay(): Promise<ApiResponse<void>> {
    return wrapDelete('/users/me/bindings/alipay')
  },

  async getAccountBindingInfo(): Promise<ApiResponse<AccountBindingInfo>> {
    return wrapGet('/users/me/bindings')
  },

  // Token Management - Test Token
  async testToken(token: string): Promise<ApiResponse<UserInfo>> {
    return wrapPost('/auth/login/test-token', { token })
  },

  // Wechat Login Flow - Get QR Code
  async getWechatLoginQrCode(): Promise<ApiResponse<WechatQrCodeResponse>> {
    return wrapPost('/auth/wechat/qrcode')
  },

  // Wechat Login Flow - Confirm Login
  async confirmWechatLogin(data: { sceneId: string, code: string }): Promise<ApiResponse<void>> {
    return wrapPost('/auth/wechat/login/confirm', data)
  },

  // Wechat Login Flow - Get Login Result
  async getWechatLoginResult(sceneId: string): Promise<ApiResponse<WechatLoginResultResponse>> {
    return wrapPost('/auth/wechat/login/result', { fingerprint: sceneId })
  },
}
